import { ApplicationElement, html } from '@crossiety/lit-components';
import { ifDefined } from 'lit/directives/if-defined.js';
import style from './style.lit.css';
import '@crossiety/lit-components/components/icon/element.js';

const allTypes = {
  image: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'svg'],
  audio: ['mp3', 'weba'],
  video: ['webm', 'mpeg', 'mp4', 'mov', 'avi'],
  pdf: ['pdf'],
  doc: ['doc', 'docx', 'odt'],
  sheet: ['ods', 'xls', 'xlsx'],
  slide: ['ppt', 'odp', 'pptx'],
  archive: ['zip', '7z'],
};

const fileType = {};

Object.entries(allTypes).forEach(([type, extensions]) =>
  extensions.forEach(ext => (fileType[ext] = type))
);

/**
 * File attachment with info
 *
 * ## Download handler
 *
 * You can set the downloadFile handler inside your Application by setting `XsAttachment.downloadFile`
 *
 * ```js
 * import XsAttachment from '@crossiety/design/components/attachment/element.js';
 * import { downloadFile } from 'src/download_file';
 *
 * XsAttachment.downloadFile = downloadFile
 *
 *
 * ```
 *
 * @prop file - Download file name
 * @prop href - Link to the file
 * @prop size - Info about file size
 * @prop layout - Different component style (options: lg, md, sm) default: 'lg', optional
 *
 * @cssprop [--background: var(--gray-100)] Background color of link wrapper
 * @cssprop [--type-margin: 0 8px 0 0] Type element margin
 * @cssprop [--type-padding: 10px 14px] Type element padding
 * @cssprop [--type-background: var(--gray-100)] Type element background color
 * @cssprop [--type-border-radius: 6px] Type element border radius
 * @cssprop [--type-icon-size: 30px] Type element icon font-size
 * @cssprop [--title-font-weight: 600] Font weight of file title
 * @cssprop [--title-font-size: var(--font-size-sm)] Font size of file title
 * @cssprop [--size-font-size: var(--font-size-xs)] Font size of file size
 */

export class XsAttachment extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    file: { type: String },
    href: { type: String },
    size: { type: String },
    layout: { type: String, reflect: true },
  };

  constructor() {
    super();
    this.layout = 'lg';
  }

  get ext() {
    const matchData = this.file.match(/\.(\w+)$/);
    return matchData && matchData[1];
  }

  get name() {
    const len = this.file.lastIndexOf('.');
    return len === -1 ? this.file : this.file.slice(0, len);
  }

  get splitedName() {
    const n = 10;
    const startName = this.name.slice(0, -n);
    const endName = this.name.slice(-n);

    return [startName, endName].filter(Boolean);
  }

  get icon() {
    const types = ['image', 'audio', 'video', 'pdf', 'archive'];
    const iconType = types.includes(this.type) ? this.type : 'alt';
    return `file-${iconType}`;
  }

  get type() {
    return fileType[this.ext] || 'other';
  }

  handleDownload(e) {
    this.constructor.downloadFile?.(e);
  }

  renderIcon() {
    if (this.layout === 'sm' && !this.ext) {
      return null;
    }

    return html`
      <div class="type type-${this.type}">
        ${this.layout === 'lg' || this.layout === 'md'
          ? html`<ef-icon name="${this.icon}"></ef-icon>`
          : ''}
        ${this.ext ? html`<span>${this.ext}</span>` : ''}
      </div>
    `;
  }

  renderInfo() {
    return html`
      <div class="info">
        <div class="title">
          ${this.splitedName.map(item => html`<span>${item}</span>`)}
        </div>
        ${this.size && (this.layout === 'lg' || this.layout === 'md')
          ? html`<div class="size">${this.size}</div>`
          : ''}
      </div>
    `;
  }

  render() {
    return html`
      <a
        class="js-download-image"
        @click=${this.handleDownload}
        href=${ifDefined(this.href)}
        download=${this.file}>
        ${this.renderIcon()} ${this.renderInfo()}
      </a>
    `;
  }
}

export default XsAttachment;

customElements.get('xs-attachment') ||
  customElements.define('xs-attachment', XsAttachment);
